import { Popconfirm, notification } from 'antd';
import { useEffect, useState } from 'react';
import { ListRulesResponse } from '../api/models';
import APIClient from '../api/api';
import { Column, DataTable, Num } from '../components/ui';
import { PageHead, PageToolbar } from '../layout';

const ListRules = () => {
  const [rules, setRules] = useState<ListRulesResponse>();
  const [loading, setLoading] = useState(false);
  const [disabling, setDisabling] = useState<number | null>(null);

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    setLoading(true);
    try {
      const resp = await APIClient.listRules();
      setRules(resp);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load rules at this time.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleDisable(id: number) {
    setDisabling(id);
    try {
      await APIClient.deactivateRule(id);
      notification.success({ message: 'Rule disabled and cleanup started.' });
      loadRules();
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to disable rule.' });
    } finally {
      setDisabling(null);
    }
  }

  const columns: Column<ListRulesResponse['rules'][number]>[] = [
    {
      key: 'name',
      title: 'Name',
      render: (r) => <span className="text-text font-medium text-[12.5px]">{r.name}</span>,
    },
    {
      key: 'totalDomains',
      title: 'Total Domains',
      align: 'right',
      render: (r) => <Num value={r.totalDomains ?? null} format="int" />,
    },
    {
      key: 'actions',
      title: '',
      width: 80,
      render: (r) => (
        <Popconfirm
          title="Disable this rule?"
          description="This will remove all domain associations via a cleanup workflow."
          onConfirm={() => handleDisable(r.id)}
          okText="Disable"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <button
            type="button"
            disabled={disabling === r.id}
            className="text-[11px] text-text-3 hover:text-danger disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {disabling === r.id ? 'disabling…' : 'Disable'}
          </button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <PageHead title="Rules" subtitle="Auto-picking rules currently in effect." />
      <PageToolbar>
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {rules ? (
            <>
              total <b className="text-text-2">{rules.rules.length.toLocaleString()}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>
      <DataTable
        columns={columns}
        data={rules?.rules ?? []}
        rowKey={(r) => String(r.id)}
        loading={loading}
        emptyMessage="No rules defined."
      />
    </>
  );
};

export default ListRules;
